"use client"

import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';
import { useTheme } from '../../../components/layout/ThemeProvider';
import ContactForm from '../../../components/ui/ContactForm';

const AutomacaoResidencialPage = () => {
  const { theme } = useTheme();
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 } 
    }
  };
  
  // Serviços de automação residencial
  const servicosAutomacao = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      title: 'Controle de Iluminação',
      description: 'Sistemas inteligentes para controle total da iluminação, cenas programáveis e sensores.'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      ),
      title: 'Controle de Acesso',
      description: 'Fechaduras smart, interfones com vídeo e integração com sistemas de segurança.'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Áudio e Vídeo',
      description: 'Distribuição multiroom, home theaters automatizados e controle centralizado.'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      title: 'Sistemas de Segurança',
      description: 'Câmeras inteligentes, alarmes e monitoramento em tempo real com alertas.'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
      ),
      title: 'Controle de Clima',
      description: 'Automação de temperatura, ventilação e persianas para conforto térmico.'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
      title: 'Controle por Voz',
      description: 'Integração com Alexa, Google Assistant e Siri para comandos por voz.'
    }
  ];

  // Benefícios da automação residencial
  const beneficios = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Conveniência',
      description: 'Controle centralizado via smartphone ou voz, mesmo à distância.'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Economia de Energia',
      description: 'Redução do consumo com dispositivos inteligentes autoajustáveis.'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: 'Segurança Avançada',
      description: 'Monitoramento em tempo real e integração de sistemas de segurança.'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      title: 'Valorização do Imóvel',
      description: 'Aumento do valor da propriedade com tecnologias de automação.'
    }
  ];

  // FAQ items - versão compacta
  const faqItems = [
    {
      pergunta: 'Quanto custa para automatizar uma residência?',
      resposta: 'O custo varia conforme o tamanho, funcionalidades e complexidade. Cada projeto é personalizado, com opção de implementação modular e gradual. Entre em contato para uma avaliação gratuita.'
    },
    {
      pergunta: 'É possível automatizar uma casa já construída?',
      resposta: 'Sim. Oferecemos soluções sem fio que não exigem obras estruturais, ideais para residências já construídas, além de sistemas híbridos que combinam tecnologias.'
    },
    {
      pergunta: 'Quais marcas e tecnologias vocês utilizam?',
      resposta: 'Preferimos o sistema Zigbee por ser confiável, modular e ter ótimo custo-benefício. Funciona independente da rede Wi-Fi e, sendo um padrão aberto, não restringe a um único fabricante.'
    },
    {
      pergunta: 'Preciso ter internet para que a automação funcione?',
      resposta: 'Não necessariamente. Nossos sistemas operam mesmo sem internet para funções básicas. Apenas o acesso remoto e algumas funcionalidades avançadas dependem de conexão.'
    },
    {
      pergunta: 'Vocês oferecem suporte técnico após a instalação?',
      resposta: 'Sim, oferecemos suporte contínuo para ajustes, solução de problemas e atualizações. Também fornecemos treinamento completo para utilização do sistema.'
    },
    {
      pergunta: 'É necessário adquirir todo o sistema de uma só vez?',
      resposta: 'Não. Nossos sistemas modulares permitem implementação gradual, começando com prioridades e expandindo conforme necessidade e orçamento.'
    }
  ];

  return (
    <>
      {/* Breadcrumbs */}
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'} py-2`}>
        <div className="container mx-auto px-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link 
                  href="/" 
                  className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Início
                </Link>
              </li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <Link 
                  href="/servicos" 
                  className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Serviços
                </Link>
              </li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <span className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} font-medium`}>
                  Automação Residencial
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className={`py-20 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} relative overflow-hidden`}
      >
        {/* Background decorativo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg 
            className="absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/4 opacity-20" 
            width="800" 
            height="800" 
            fill="none" 
            viewBox="0 0 800 800"
            aria-hidden="true"
          >
            <circle 
              cx="400" 
              cy="400" 
              r="400" 
              fill={theme === 'dark' ? "#3B82F6" : "#60A5FA"} 
              fillOpacity="0.2" 
            />
          </svg>
          
          <svg 
            className="absolute left-0 bottom-0 transform -translate-x-1/4 translate-y-1/4 opacity-20" 
            width="800" 
            height="800" 
            fill="none" 
            viewBox="0 0 800 800"
            aria-hidden="true"
          >
            <circle 
              cx="400" 
              cy="400" 
              r="400" 
              fill={theme === 'dark' ? "#4F46E5" : "#818CF8"} 
              fillOpacity="0.2" 
            />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div 
              initial="hidden" 
              animate="visible" 
              variants={fadeIn}
              className="lg:w-1/2"
            >
              <div className={`inline-block px-3 py-1 text-sm font-semibold ${
                theme === 'dark' 
                  ? 'text-blue-300 bg-blue-900/50' 
                  : 'text-blue-800 bg-blue-100'
                } rounded-full mb-3`}>
                Automação Residencial
              </div>
              <h1 className={`text-4xl lg:text-5xl font-bold leading-tight ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-6`}>
                Transforme sua Casa em uma <span className="text-blue-600">Casa Inteligente</span>
              </h1>
              <p className={`text-xl ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              } mb-8`}>
                Soluções personalizadas de automação para controlar iluminação, 
                segurança, climatização e entretenimento com o máximo de conforto e tecnologia.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.a 
                  href="#contato" 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-lg tracking-wide rounded-lg shadow-lg hover:shadow-xl transition-all text-center"
                  style={{
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    color: '#ffffff'
                  }}
                >
                  Solicitar Orçamento
                </motion.a>
                <motion.a 
                  href="#servicos" 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-8 py-3 ${
                    theme === 'dark' 
                      ? 'bg-gray-800 hover:bg-gray-700 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  } font-extrabold text-lg tracking-wide rounded-lg shadow hover:shadow-lg transition-all text-center`}
                >
                  Conhecer Soluções
                </motion.a>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:w-1/2"
            >
              <div className="relative">
                <div className={`w-full h-96 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-blue-50'
                } flex items-center justify-center overflow-hidden`}>
                  <Image
                    src="/images/automacao-residencial-illustration.svg"
                    alt="Automação Residencial"
                    width={500}
                    height={400}
                    className="object-contain max-w-full max-h-full"
                  />
                </div>
                
                {/* Estatística flutuante - mudado para "Conforto Inteligente" */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                  className={`absolute -bottom-6 -left-6 p-6 rounded-lg shadow-xl ${
                    theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                  }`}
                >
                  <p className="text-blue-600 font-bold text-2xl">Conforto Inteligente</p>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} text-sm`}>
                    para toda sua residência
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Serviços Section */}
      <section 
        id="servicos"
        className={`py-20 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-3`}>
              Nossas Soluções
            </div>
            <h2 className={`text-3xl md:text-4xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-4`}>
              Sistemas Integrados para sua Casa Inteligente
            </h2>
            <p className={`text-xl ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Oferecemos soluções completas e personalizadas para cada ambiente da sua residência
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicosAutomacao.map((servico, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`${
                  theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                } rounded-xl p-6 shadow-xl border ${
                  theme === 'dark' ? 'border-gray-800' : 'border-gray-100'
                } h-full`}
              >
                <div className={`w-14 h-14 mb-6 rounded-xl ${
                  theme === 'dark' 
                    ? 'bg-gradient-to-br from-blue-900 to-blue-800 text-blue-300' 
                    : 'bg-gradient-to-br from-blue-500 to-blue-600 text-white'
                  } flex items-center justify-center`}>
                  {servico.icon}
                </div>
                
                <h3 className={`text-xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                } mb-4`}>{servico.title}</h3>
                
                <p className={`${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {servico.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefícios Section */}
      <section className={`py-20 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-3`}>
              Vantagens
            </div>
            <h2 className={`text-3xl md:text-4xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-4`}>
              Por que Automatizar sua Residência?
            </h2>
            <p className={`text-xl ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Experimente um novo nível de conforto e conveniência em seu lar
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="grid sm:grid-cols-2 gap-6">
                {beneficios.map((beneficio, index) => (
                  <div 
                    key={index}
                    className={`p-6 rounded-xl ${
                      theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-100'
                    }`}
                  >
                    <div className={`w-12 h-12 mb-4 rounded-xl ${
                      theme === 'dark' 
                        ? 'bg-blue-900/40 text-blue-400' 
                        : 'bg-blue-100 text-blue-600'
                      } flex items-center justify-center`}>
                      {beneficio.icon}
                    </div>
                    <h3 className={`text-xl font-bold mb-2 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>{beneficio.title}</h3>
                    <p className={`${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {beneficio.description}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className={`p-8 rounded-xl overflow-hidden ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
              } shadow-xl border ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <h3 className={`text-2xl font-bold mb-6 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Tecnologia a Serviço do seu Bem-estar</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-1 mt-1 rounded-full ${
                      theme === 'dark' ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-600'
                    }`}>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                      <strong>Modernidade</strong> que valoriza seu imóvel
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className={`p-1 mt-1 rounded-full ${
                      theme === 'dark' ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-600'
                    }`}>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                      <strong>Sustentabilidade</strong> com redução de consumo
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className={`p-1 mt-1 rounded-full ${
                      theme === 'dark' ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-600'
                    }`}>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                      <strong>Tranquilidade</strong> com monitoramento remoto
                    </p>
                  </div>
                </div>
                
                <div className={`mt-8 p-4 rounded-lg ${
                  theme === 'dark' ? 'bg-blue-900/20 border border-blue-900/30' : 'bg-blue-50 border border-blue-100'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-1 rounded-full ${
                      theme === 'dark' ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-600'
                    } mt-1`}>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className={`text-sm ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
                      <strong>Instalações</strong> duráveis e adaptáveis às novas tecnologias.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="absolute -top-4 -right-4 transform rotate-6">
                <div className={`px-4 py-2 rounded-lg ${
                  theme === 'dark' ? 'bg-green-900/70 text-green-300' : 'bg-green-600 text-white'
                } font-bold shadow-lg`}>
                  Tecnologia do Futuro
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Como Funciona */}
      <section className={`py-20 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-3`}>
              Processo
            </div>
            <h2 className={`text-3xl md:text-4xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-4`}>
              Como Funciona Nosso Serviço
            </h2>
            <p className={`text-xl ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Do projeto à implementação, nosso processo é transparente e personalizado
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Linha do tempo vertical */}
              <div className={`absolute left-9 top-0 bottom-0 w-1 ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
              }`}></div>
              
              {/* Etapas do processo */}
              <div className="space-y-12">
                {[
                  {
                    number: 1,
                    title: 'Avaliação Inicial',
                    description: 'Entendemos suas necessidades e avaliamos a estrutura existente.'
                  },
                  {
                    number: 2,
                    title: 'Projeto Personalizado',
                    description: 'Desenvolvemos um projeto sob medida para sua residência e orçamento.'
                  },
                  {
                    number: 3,
                    title: 'Instalação Profissional',
                    description: 'Nossa equipe realiza a instalação com mínima intervenção estrutural.'
                  },
                  {
                    number: 4,
                    title: 'Configuração e Testes',
                    description: 'Configuramos todos os dispositivos e realizamos testes de integração.'
                  },
                  {
                    number: 5,
                    title: 'Treinamento e Suporte',
                    description: 'Treinamento completo e suporte contínuo para máximo aproveitamento.'
                  }
                ].map((etapa, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-start relative"
                  >
                    <div className={`w-18 h-18 rounded-full z-10 flex items-center justify-center ${
                      theme === 'dark' 
                        ? 'bg-blue-900 text-blue-100' 
                        : 'bg-blue-600 text-white'
                    } font-bold text-xl shadow-lg`} style={{width: '3rem', height: '3rem'}}>
                      {etapa.number}
                    </div>
                    <div className={`ml-8 p-6 rounded-lg ${
                      theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                    } shadow-lg`}>
                      <h3 className={`text-xl font-bold mb-2 ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>{etapa.title}</h3>
                      <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                        {etapa.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <motion.a 
              href="#contato" 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`inline-flex items-center px-6 py-3 rounded-lg font-extrabold text-lg tracking-wide ${
                theme === 'dark' 
                  ? 'bg-blue-700 hover:bg-blue-600 text-white' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              } shadow-lg hover:shadow-xl transition-all`}
              style={{
                textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                color: '#ffffff'
              }}
            >
              <span>Começar Meu Projeto</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </motion.a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        id="faq"
        className={`py-20 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-3`}>
              FAQ
            </div>
            <h2 className={`text-3xl md:text-4xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-4`}>
              Perguntas Frequentes sobre Automação Residencial
            </h2>
            <p className={`text-xl ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Tire suas dúvidas sobre nossos serviços de automação para sua casa
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            {faqItems.map((item, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`mb-6 rounded-xl ${
                  theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'
                } shadow-md overflow-hidden`}
                itemScope
                itemType="https://schema.org/Question"
              >
                <details className="group">
                  <summary className={`flex items-center justify-between p-6 cursor-pointer ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    <h3 className="text-lg font-semibold" itemProp="name">{item.pergunta}</h3>
                    <span className="relative ml-2 flex-shrink-0">
                      <svg 
                        className={`w-5 h-5 transition-transform transform ${
                          theme === 'dark' ? 'text-blue-300' : 'text-blue-600'
                        } group-open:rotate-180`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </span>
                  </summary>
                  <div 
                    className={`px-6 pb-6 pt-0 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                    itemScope
                    itemProp="acceptedAnswer" 
                    itemType="https://schema.org/Answer"
                  >
                    <p itemProp="text">{item.resposta}</p>
                  </div>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        id="contato"
        className={`py-20 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} relative overflow-hidden`}
      >
        {/* Background decorativo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg 
            className="absolute top-0 left-0 transform -translate-x-1/2 -translate-y-1/2" 
            width="600" 
            height="600" 
            fill="none" 
            viewBox="0 0 600 600"
            aria-hidden="true"
          >
            <circle 
              cx="300" 
              cy="300" 
              r="300" 
              fill={theme === 'dark' ? "#1E40AF" : "#3B82F6"} 
              fillOpacity="0.05" 
            />
          </svg>
          
          <svg 
            className="absolute bottom-0 right-0 transform translate-x-1/2 translate-y-1/2" 
            width="600" 
            height="600" 
            fill="none" 
            viewBox="0 0 600 600"
            aria-hidden="true"
          >
            <circle 
              cx="300" 
              cy="300" 
              r="300" 
              fill={theme === 'dark' ? "#4F46E5" : "#818CF8"} 
              fillOpacity="0.05" 
            />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
            >
              <div className={`inline-block px-3 py-1 text-sm font-semibold ${
                theme === 'dark' 
                  ? 'text-blue-300 bg-blue-900/50' 
                  : 'text-blue-800 bg-blue-100'
                } rounded-full mb-3`}>
                Vamos Conversar
              </div>
              <h2 className={`text-3xl md:text-4xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-4`}>
                Transforme sua Casa em um Ambiente Inteligente
              </h2>
              <p className={`text-xl ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              } mb-8`}>
                Entre em contato para uma avaliação gratuita. Nossos especialistas irão entender suas necessidades e apresentar as melhores soluções de automação residencial.
              </p>
              
              <div className="space-y-6">
                <div className={`flex items-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <div className={`w-12 h-12 rounded-full ${
                    theme === 'dark' ? 'bg-blue-900/40' : 'bg-blue-100'
                  } flex items-center justify-center mr-4 ${
                    theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                  }`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Telefone</p>
                    <a 
                      href="tel:+551932060706" 
                      className={`${
                        theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'
                      }`}
                    >
                      (19) 3206-0706
                    </a>
                  </div>
                </div>
                
                <div className={`flex items-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <div className={`w-12 h-12 rounded-full ${
                    theme === 'dark' ? 'bg-green-900/40' : 'bg-green-100'
                  } flex items-center justify-center mr-4 ${
                    theme === 'dark' ? 'text-green-300' : 'text-green-700'
                  }`}>
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">WhatsApp</p>
                    <a 
                      href="https://wa.me/5519994799400" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${
                        theme === 'dark' ? 'text-green-300 hover:text-green-200' : 'text-green-600 hover:text-green-800'
                      }`}
                    >
                      (19) 99479-9400
                    </a>
                  </div>
                </div>
                
                <div className={`flex items-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <div className={`w-12 h-12 rounded-full ${
                    theme === 'dark' ? 'bg-blue-900/40' : 'bg-blue-100'
                  } flex items-center justify-center mr-4 ${
                    theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                  }`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Email</p>
                    <a 
                      href="mailto:contato@webimports.com.br" 
                      className={`${
                        theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'
                      }`}
                    >
                      contato@webimports.com.br
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              className={`${
                theme === 'dark' ? 'bg-gray-900 border border-gray-700' : 'bg-white shadow-xl'
              } rounded-xl p-6`}
            >
              <h3 className={`text-xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-6`}>
                Solicite uma avaliação gratuita
              </h3>
              
              <ContactForm />
              
              <p className={`mt-4 text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Ao enviar este formulário, você concorda com nossa política de privacidade.
                Seus dados não serão compartilhados com terceiros.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AutomacaoResidencialPage;